import React from 'react';

// Design System Components
import Tag from 'Components/DesignSystem/Tag/Tag';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';
import Typography from 'Components/DesignSystem/Typography/Typography';

// Module Components
import SectionHeading from './SectionHeading';

// Constants
import { MAX_CUSTOM_CSS_LENGTH, STRINGS } from '../../../constants';

// Types
import { FieldId } from '../../../types';
// i18n — the character counter is a plural message, not a number glued to a noun.
import { plural } from '../../../i18n';

interface CustomStylingSectionProps {
  fieldId: FieldId;
  /** The element's own CSS selector, from the `FIELD_SELECTORS` table (R67). */
  selector: string;
  /** The element's short name, used in the control's accessible name. */
  panelName: string;
  customCss: string;
  onCustomCssChange: (customCss: string) => void;
}

/**
 * The Custom styling section: a per-element CSS editor, applied live (spec R67).
 *
 * Every element's panel has one (spec R66). The chip states which selector the
 * declarations land on, the input holds **only** that element's declarations — no
 * selector, no braces — and the counter under it counts the characters as typed, so
 * it matches what the user sees rather than what survives parsing.
 *
 * A plain `<textarea>` and not a code editor: NFR A1 requires the control not to
 * trap Tab. Nothing here interprets the text; it goes to the reducer as typed and
 * `styling/` is what confines it on the way to the preview (R68, ADR-4A).
 */
const CustomStylingSection = ({
  fieldId,
  selector,
  panelName,
  customCss,
  onCustomCssChange,
}: CustomStylingSectionProps) => {
  const counterId = `product-card-custom-css-counter-${fieldId}`;

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-px-3 tw-py-3 tw-border-b tw-border-edge">
      <SectionHeading as="h4">{STRINGS.pane.customStyling}</SectionHeading>

      <div>
        <Tag label={`${selector} { … }`} tone="info" className="tw-font-noto-sans-mono" />
      </div>

      <Textarea
        label=""
        aria-label={`${STRINGS.pane.customCssLabel} ${panelName}`}
        placeholder={STRINGS.pane.customCssPlaceholder}
        value={customCss}
        onChange={onCustomCssChange}
        maxLength={MAX_CUSTOM_CSS_LENGTH}
        rows={6}
        spellCheck={false}
        aria-describedby={counterId}
      />

      {/* The id has to sit on an element `aria-describedby` can name, and
          `Typography` owns its own attributes — hence the wrapper. */}
      <span id={counterId}>
        <Typography
          variant="caption-metadata"
          as="span"
          className="tw-text-ink-tertiary tw-font-noto-sans-mono"
        >
          {plural(customCss.length, STRINGS.pane.customCssCounter)}
        </Typography>
      </span>
    </div>
  );
};

export default CustomStylingSection;
